import { FormEvent } from 'react';
import { useTranslation } from 'next-i18next';
import { Input, Label, Textarea } from '@components/common/Forms';
import { bjToast, Button } from '@components/common';
import { useBjForm } from '@hooks/useBjForm';
import { useSwrJobInterviewSaveTextAnswerList } from '@hooks/useSwrApi/endpoints/job-interview/useSwrJobInterviewSaveTextAnswerList';

interface InterviewQuestionsProps {
  jobQuestion: ApiSchema<'InterviewUserQuestionRead'>[];
  locale: Locale;
  onSaveSuccess: () => void,
}

export const TextInterviewForm = (props: InterviewQuestionsProps) => {
  const {
    jobQuestion, locale, onSaveSuccess,
  } = props;

  const { t } = useTranslation('common');

  const {
    record, checkFormValidation,
  } = useBjForm({});

  const { trigger: saveTextAnswerList, isMutating: isLoading } = useSwrJobInterviewSaveTextAnswerList({
    locale,
    successCallback: () => {
      onSaveSuccess();
    },
    errorCallback: (error) => {
      bjToast.error(error.errors[0].message);
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = checkFormValidation(event);
    if (!formData || typeof formData !== 'object') {
      return;
    }

    // Transform the form data from bracket notation object to array
    const transformedData = {
      textAnswers: jobQuestion.map((item, index) => {
        const textAnswerData = (formData as Record<string, { answerText: string; questionId: string }>)[`textAnswers[${index}]`];
        return {
          questionId: Number(textAnswerData?.questionId || item.questionId),
          answerText: textAnswerData?.answerText || '',
        };
      }),
    };

    void saveTextAnswerList(transformedData);
  };

  // Render the form
  // **************************
  return (
    <div>
      <p className="pb-6 text-lg">
        {t('apply.wizard.mini_interview.subtitle')}
      </p>
      <form noValidate onSubmit={onSubmit}>
        {
          jobQuestion.map((item, index) => (
            <div key={item.questionId} className="mb-8">
              <Label
                htmlFor={`textAnswers[${index}].answerText`}
                description={`#${index + 1}. ${item.question}`}
                descriptionClass="text-md font-bold"
                required
              >
                <Textarea
                  minLength={3}
                  required
                  {...record(`textAnswers[${index}].answerText`)}
                  placeholder={t('global.placeholder.answer')}
                />
                <Input type="hidden" {...record(`textAnswers[${index}].questionId`)} value={item.questionId} />
              </Label>
            </div>
          ))
        }
        <div className="flex flex-col sm:items-start md:items-center">
          <Button
            type="submit"
            styling="solid"
            color="secondary"
            size="md"
            className="mb-2 w-full rounded-full md:mb-6 md:w-auto"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t('global.action.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};
